import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { api } from '../../../../services/Main/Api'
import Masks from '../../../../components/Shared/Masks'
import moment from 'moment/moment'

const STATUS_COLOR = {
    PAID: 'success',
    PENDING: 'warning',
    SCHEDULED: 'secondary',
    FINALIZED: 'success',
    PROCESSING: 'secondary',
    ERROR: 'error',
    DUE: 'error',
    DEBIT: 'error',
    FAILED: 'error',
    CANCELLED: 'error',
    OPEN: 'info',
}

const PayoutDetailDialog = ({ open, transferId, onClose }) => {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!open || !transferId) return
        setLoading(true)
        setData(null)
        api.get(`signed-recipient-transfer/${transferId}/`)
            .then(res => setData(res.data))
            .catch(err => console.error(err))
            .finally(() => setLoading(false))
    }, [open, transferId])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            scroll="paper"
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" color="primary.main">
                    Detalhes do Repasse
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : data ? (
                    <Box>
                        {/* Header: número + status */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" color="primary.main">
                                {data.number}
                            </Typography>
                            <Chip
                                color={STATUS_COLOR[data?.status?.id] || 'default'}
                                size="small"
                                variant="outlined"
                                label={data?.status?.value}
                            />
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Tabela de taxas */}
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Código</TableCell>
                                    <TableCell>Descrição</TableCell>
                                    <TableCell width={150}>Valor</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data?.fees?.map(fee => (
                                    <TableRow key={fee.id}>
                                        <TableCell>COB-{fee.id}</TableCell>
                                        <TableCell>{fee.description}</TableCell>
                                        <TableCell sx={fee.total < 0 ? { color: 'error.main' } : {}}>
                                            {Masks.money(fee.total.toString())}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {data?.percentage < 100 && (
                                    <TableRow>
                                        <TableCell />
                                        <TableCell align="right">
                                            <Typography fontWeight="bold">Subtotal:</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography fontWeight="bold">
                                                {Masks.money(data?.subtotal?.toString())}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {data?.income_tax && (
                                    <TableRow>
                                        <TableCell />
                                        <TableCell align="right">
                                            <Typography>Imposto de renda retido na fonte:</Typography>
                                        </TableCell>
                                        <TableCell>
                                            {Masks.money(data?.income_tax?.toString())}
                                        </TableCell>
                                    </TableRow>
                                )}
                                {data?.percentage < 100 && (
                                    <TableRow>
                                        <TableCell />
                                        <TableCell colSpan={2} align="right">
                                            <Chip label={`Beneficiário possui somente ${data?.percentage}% do repasse`} />
                                        </TableCell>
                                    </TableRow>
                                )}
                                <TableRow>
                                    <TableCell />
                                    <TableCell align="right">
                                        <Typography sx={{ fontWeight: 'bold' }} color={data?.total < 0 ? 'error.main' : undefined}>
                                            Total do Repasse:
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }} color={data?.total < 0 ? 'error.main' : undefined}>
                                            {Masks.money(data?.total?.toString())}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>

                        {/* Contrato */}
                        {data?.rent_contract && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle1" color="primary.main" gutterBottom fontWeight="bold">
                                    Contrato
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                            <TableCell>{data.rent_contract.number}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                                            <TableCell>{data.rent_contract.name}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                                            <TableCell>{data.rent_contract.contract_type}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                            <TableCell>{data.rent_contract.status}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {Masks.money(data.rent_contract.value?.toString())}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        {/* Fatura */}
                        {data?.invoice && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle1" color="primary.main" gutterBottom fontWeight="bold">
                                    Fatura
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Nº da Fatura</TableCell>
                                            <TableCell>{data.invoice.number}</TableCell>
                                        </TableRow>
                                        {data.invoice.unit && (
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Imóvel</TableCell>
                                                <TableCell>{data.invoice.unit}</TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Vencimento</TableCell>
                                            <TableCell>
                                                {data.invoice.due_date
                                                    ? moment(data.invoice.due_date).format('DD/MM/YYYY')
                                                    : '-'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                            <TableCell>{data.invoice.status}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {data.invoice.total?.toString().includes('-') && '- '}
                                                {Masks.money(data.invoice.total?.toString())}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        {/* Beneficiário */}
                        {data?.contract_recipient && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle1" color="primary.main" gutterBottom fontWeight="bold">
                                    Beneficiário
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                                            <TableCell>{data.contract_recipient.name}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>E-mail</TableCell>
                                            <TableCell>{data.contract_recipient.email}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Telefone</TableCell>
                                            <TableCell>{data.contract_recipient.phone}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>CPF/CNPJ</TableCell>
                                            <TableCell>{data.contract_recipient.cpf_cnpj}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        {/* Dados Bancários - TED */}
                        {data?.bank_account_recipient?.account_type === 'TED' && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle1" color="primary.main" gutterBottom fontWeight="bold">
                                    Dados Bancários
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Banco</TableCell>
                                            <TableCell>{data.bank_account_recipient.bank}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Tipo da conta</TableCell>
                                            <TableCell>{data.bank_account_recipient.bank_account_type}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Agência</TableCell>
                                            <TableCell>
                                                {data.bank_account_recipient.bank_branch}
                                                {data.bank_account_recipient.bank_branch_digit
                                                    ? `-${data.bank_account_recipient.bank_branch_digit}`
                                                    : ''}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Conta</TableCell>
                                            <TableCell>
                                                {data.bank_account_recipient.bank_account}
                                                {data.bank_account_recipient.bank_account_digit
                                                    ? `-${data.bank_account_recipient.bank_account_digit}`
                                                    : ''}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        {/* Dados Bancários - PIX */}
                        {data?.bank_account_recipient?.account_type === 'PIX' && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle1" color="primary.main" gutterBottom fontWeight="bold">
                                    Dados do Pix
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Banco</TableCell>
                                            <TableCell>{data.bank_account_recipient.bank}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Chave Pix</TableCell>
                                            <TableCell>{data.bank_account_recipient.pix_key_type}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Chave</TableCell>
                                            <TableCell>{data.bank_account_recipient.pix_key_value}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Box>
                ) : null}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    )
}

export default PayoutDetailDialog
